import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  numeric,
  boolean,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Status/type constants
// ---------------------------------------------------------------------------

export const CHURN_MODEL_TYPES = ["churn", "ltv_prediction", "engagement_scoring"] as const;
export type ChurnModelType = (typeof CHURN_MODEL_TYPES)[number];

export const PROPOSAL_TYPES = ["add_step", "remove_step", "modify_timing", "change_condition", "reorder_steps"] as const;
export type AutomationProposalType = (typeof PROPOSAL_TYPES)[number];

export const AUTOMATION_PROPOSAL_STATUSES = ["proposed", "reviewed", "approved", "applied", "rejected"] as const;
export type AutomationProposalStatus = (typeof AUTOMATION_PROPOSAL_STATUSES)[number];

// ---------------------------------------------------------------------------
// 1. Churn Model Configs — Per-tenant prediction model configuration (F13)
// ---------------------------------------------------------------------------

export const churnModelConfigs = pgTable("churn_model_configs", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  // AC3: modelType for extensibility (churn, LTV, engagement)
  modelType: text("model_type").notNull(), // ChurnModelType
  modelVersion: text("model_version").notNull().default("v1"),
  featureWeights: jsonb("feature_weights").$type<Record<string, number>>().notNull().default({}),
  threshold: numeric("threshold", { precision: 5, scale: 4 }).notNull().default("0.5000"),
  lookbackDays: integer("lookback_days").notNull().default(90),
  isActive: boolean("is_active").notNull().default(true),
  lastTrainedAt: timestamp("last_trained_at", tz),
  accuracy: numeric("accuracy", { precision: 5, scale: 4 }), // 0.0000-1.0000
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_churn_model_tenant").on(table.tenantId),
  // AC3: Only one active model per type per tenant
  uniqueIndex("uq_churn_model_tenant_type").on(table.tenantId, table.modelType),
]);

// ---------------------------------------------------------------------------
// 2. Automation Proposals — AI-suggested workflow improvements (F17 Agentic AI)
// ---------------------------------------------------------------------------
// EU AI Act: AI suggests, human approves. approvedBy MUST be set before
// proposal execution (service-layer enforced, not DB constraint).

export const automationProposals = pgTable("automation_proposals", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  workflowId: text("workflow_id").notNull(), // References workflow in KV or DB
  proposalType: text("proposal_type").notNull(), // AutomationProposalType
  description: text("description").notNull(),
  proposedChanges: jsonb("proposed_changes").$type<Record<string, unknown>>().notNull(),
  confidence: numeric("confidence", { precision: 3, scale: 2 }), // 0.00-1.00
  status: text("status").notNull().default("proposed"), // AutomationProposalStatus
  // CQ5: EU AI Act — MUST be set before proposal execution (service-layer enforced)
  approvedBy: uuid("approved_by"), // Nullable until approved — mandatory for execution
  appliedAt: timestamp("applied_at", tz),
  rejectedAt: timestamp("rejected_at", tz),
  rejectionReason: text("rejection_reason"),
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
}, (table) => [
  index("idx_auto_proposal_tenant").on(table.tenantId),
  index("idx_auto_proposal_workflow").on(table.tenantId, table.workflowId),
  index("idx_auto_proposal_status").on(table.tenantId, table.status),
]);

// ---------------------------------------------------------------------------
// 3. Automation Learnings — What the AI learned from tenant behavior
// ---------------------------------------------------------------------------
// APPEND-ONLY: Once a learning is recorded, it is not mutated.
// EU AI Act audit trail — all AI decisions must be traceable.
// insight MUST NEVER contain PII — only anonymized behavioral patterns.

export const automationLearnings = pgTable("automation_learnings", {
  id: uuid("id").primaryKey().defaultRandom(),
  tenantId: uuid("tenant_id").notNull(),
  proposalId: uuid("proposal_id"), // Nullable: learning may come from an applied proposal or observation
  learningType: text("learning_type").notNull(), // "timing_pattern" | "audience_segment" | "content_preference" | "channel_preference"
  insight: jsonb("insight").$type<Record<string, unknown>>().notNull(), // Anonymized behavioral insight — NO PII
  source: text("source").notNull(), // "applied_proposal" | "rejected_proposal" | "behavioral_observation"
  createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  // AC4: No updatedAt — append-only (EU AI Act audit trail)
}, (table) => [
  index("idx_auto_learning_tenant").on(table.tenantId),
  index("idx_auto_learning_type").on(table.tenantId, table.learningType),
]);
